import React, { useEffect, useState, createContext, useContext } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Heart, User, LogOut, Package, Menu, ShoppingBag, Phone, Mail, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR, resolveImg } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSiteContent } from "@/context/SiteContentContext";

// True only inside <StorefrontShell/>, which already renders the chrome once.
const ShellCtx = createContext(false);

const BrandLogo = ({ content = {}, loaded }) => {
  const [logoReady, setLogoReady] = useState(false);
  const logoSrc = content.logo_image ? resolveImg(content.logo_image) : "";
  useEffect(() => { setLogoReady(false); }, [logoSrc]);
  if (logoSrc) {
    return (
      <span className="relative inline-flex h-9 sm:h-11 items-center">
        <img
          src={logoSrc}
          alt={content.logo_text || "Maahey Jewels"}
          onLoad={() => setLogoReady(true)}
          onError={() => setLogoReady(true)}
          fetchPriority="high"
          decoding="async"
          className="h-9 sm:h-11 w-auto max-w-[180px] object-contain transition-opacity duration-200"
          style={{ opacity: logoReady ? 1 : 0 }}
        />
        {!logoReady && (
          <div className="absolute inset-y-0 left-0 w-[120px] rounded bg-muted/50 animate-pulse" aria-hidden="true" />
        )}
      </span>
    );
  }
  // While the branding fetch is still in flight, render an invisible placeholder of the
  // same footprint instead of the text fallback — this is what prevented the flicker:
  // the fallback name/subtext only ever appears once we KNOW no logo image is configured.
  if (!loaded) {
    return <div className="h-9 sm:h-11 w-[120px]" aria-hidden="true" />;
  }
  return (
    <div className="leading-none">
      <div className="font-display text-2xl sm:text-3xl tracking-wide text-[hsl(var(--primary))]">{content.logo_text || "MAAHEY"}</div>
      <div className="text-[10px] sm:text-xs tracking-[0.4em] text-[hsl(var(--gold-dark))] ml-0.5">{content.logo_subtext || "JEWELS"}</div>
    </div>
  );
};

const Chrome = ({ children }) => {
  const navigate = useNavigate();
  const { shop, shopLoggedIn, logoutShop, cartCount, wishlistIds } = useAuth();
  const { content, goldRate, categories, loaded } = useSiteContent();
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    api.get("/business/public").then(({ data }) => setBusiness(data)).catch(() => {});
  }, []);

  const iconBtn = "relative tap-target-44 text-foreground/80 hover:text-[hsl(var(--primary))] hover:bg-transparent";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Announcement / rate strip */}
      <div className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs sm:text-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
          <span className="tracking-wide truncate">{content.announcement || "Wholesale prices · Certified stones · Transparent breakup"}</span>
          {goldRate && <span className="font-mono shrink-0 ml-3">Gold (24K): {formatINR(goldRate)}/g</span>}
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[hsl(var(--card))]/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center gap-3">
          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="lg:hidden relative tap-target-44"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader><SheetTitle className="font-display text-[hsl(var(--primary))]">Categories</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {categories.map((c) => (
                  <SheetClose asChild key={c.slug}>
                    <Link to={`/products?category=${encodeURIComponent(c.name)}`} className="px-3 py-2.5 rounded-lg text-sm hover:bg-[hsl(var(--sand))] transition-colors">{c.name}</Link>
                  </SheetClose>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" data-testid="brand-logo" className="shrink-0"><BrandLogo content={content} loaded={loaded} /></Link>

          <nav className="hidden lg:flex items-center gap-6 ml-6 shrink-0">
            {categories.slice(0, 6).map((c) => (
              <Link key={c.slug} to={`/products?category=${encodeURIComponent(c.name)}`}
                className="text-sm text-foreground/75 hover:text-[hsl(var(--primary))] transition-colors duration-200 whitespace-nowrap">
                {c.name}
              </Link>
            ))}
          </nav>

          {/* Search only shares row 1 from lg+, where there's finally room alongside the
              category nav + 4 icon buttons — see the dedicated mobile/tablet row below. */}
          <div className="hidden lg:block flex-1 min-w-0 max-w-md ml-6">
            <SearchBar testidPrefix="header-search" placeholder="Search jewellery..." />
          </div>

          <div className="flex items-center gap-0.5 shrink-0 ml-auto lg:ml-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="nav-contact-button" variant="ghost" size="icon" className={iconBtn} aria-label="Contact Us">
                  <Phone className="h-5 w-5" strokeWidth={1.6} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64">
                <div className="font-display text-base mb-2">Contact Us</div>
                <div className="space-y-2 text-sm">
                  {business?.phone && (
                    <a data-testid="contact-phone-link" href={`tel:${business.phone}`} className="flex items-center gap-2 text-foreground/80 hover:text-[hsl(var(--primary))] transition-colors duration-150">
                      <Phone className="h-4 w-4 shrink-0" /> {business.phone}
                    </a>
                  )}
                  {business?.email && (
                    <a data-testid="contact-email-link" href={`mailto:${business.email}`} className="flex items-center gap-2 text-foreground/80 hover:text-[hsl(var(--primary))] transition-colors duration-150">
                      <Mail className="h-4 w-4 shrink-0" /> {business.email}
                    </a>
                  )}
                  {(business?.address_line1 || business?.city) && (
                    <div className="flex items-start gap-2 text-foreground/80">
                      <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{[business.address_line1, business.address_line2, business.city, business.state, business.pincode].filter(Boolean).join(", ")}</span>
                    </div>
                  )}
                  {!business?.phone && !business?.email && (
                    <p className="text-muted-foreground">We'll have our contact details up shortly — please check back soon.</p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button data-testid="nav-wishlist-button" variant="ghost" size="icon" onClick={() => navigate("/wishlist")} className={iconBtn}>
              <Heart className="h-5 w-5" strokeWidth={1.6} />
              {wishlistIds.length > 0 && <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] bg-[hsl(var(--primary))]">{wishlistIds.length}</Badge>}
            </Button>
            {shopLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="nav-account-button" variant="ghost" size="icon" className={iconBtn}><User className="h-5 w-5" strokeWidth={1.6} /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm">
                    <div className="font-medium">{shop?.shop_name}</div>
                    <div className="text-xs text-muted-foreground">{shop?.phone}</div>
                    {shop?.tier && (
                      <Badge className="mt-1.5 bg-[hsl(var(--gold-light))] text-[hsl(var(--gold-dark))] border border-[hsl(var(--border))]">{shop.tier} Partner</Badge>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="nav-orders-link" onClick={() => navigate("/orders")}><Package className="h-4 w-4 mr-2" /> My Orders</DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout-button" onClick={() => { logoutShop(); navigate("/login"); }}><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="nav-account-button" variant="ghost" size="icon" onClick={() => navigate("/login")} className={iconBtn}><User className="h-5 w-5" strokeWidth={1.6} /></Button>
            )}
            <Button data-testid="nav-cart-button" variant="ghost" size="icon" onClick={() => navigate("/cart")} className={iconBtn}>
              <ShoppingBag className="h-5 w-5" strokeWidth={1.6} />
              {cartCount > 0 && <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] bg-[hsl(var(--primary))]">{cartCount}</Badge>}
            </Button>
          </div>
        </div>

        {/* Mobile/tablet search — its own full-width row below the logo/icons, since there's
            no room to squeeze a usable search box into row 1 alongside 4 icon buttons on a
            phone-width screen. Still always visible (no tap-to-expand icon), just stacked
            instead of inline — the same pattern most mobile jewellery/e-commerce sites use. */}
        <div className="lg:hidden pb-3">
          <SearchBar testidPrefix="header-search-mobile" placeholder="Search jewellery..." />
        </div>
        </div>
      </header>

      {/* min-height keeps <main> at least a viewport tall. During a crossfade the
          outgoing page is taken out of layout flow (popLayout), so a tall page ->
          short page navigation used to collapse the height by ~1500px in one frame,
          slamming the footer up the screen — which read as a white flash. */}
      <main className="flex-1 relative min-h-[calc(100vh-4rem)]">{children}</main>

      {/* Footer */}
      <footer className="mt-16 bg-[hsl(var(--secondary))] border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <BrandLogo content={content} loaded={loaded} />
            <p className="text-sm text-muted-foreground mt-3 max-w-xs">{content.footer_tagline || "Fine jewellery with transparent, itemised pricing for gold, diamonds and colour stones."}</p>
          </div>
          <div>
            <div className="text-sm font-medium mb-2">Shop</div>
            <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
              {categories.slice(0,5).map(c => <Link key={c.slug} to={`/products?category=${encodeURIComponent(c.name)}`} className="hover:text-foreground">{c.name}</Link>)}
            </div>
          </div>
        </div>
        <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">© {new Date().getFullYear()} MAAHEY JEWELS. All rights reserved.</div>
      </footer>
    </div>
  );
};

/* ---------------------------------------------------------------------------
   L2 SUPPORT FIX (2026-09-04)
   The storefront chrome (header/logo/footer) AND the page-transition
   AnimatePresence used to be rendered INSIDE every page component, so each
   navigation destroyed and rebuilt them. Two consequences, both reported by
   the customer for days:
     1) the page transition could never run - AnimatePresence was unmounted
        together with the outgoing page, so no exit animation was possible and
        the entrance was suppressed (every arrival looked like a first mount);
     2) the header/logo were rebuilt on every click, which is the logo flicker.
   StorefrontShell mounts the chrome ONCE as a layout route, so it survives
   navigation, the crossfade actually plays, and the logo stops repainting.
--------------------------------------------------------------------------- */
export const StorefrontShell = () => {
  const location = useLocation();
  return (
    <ShellCtx.Provider value={true}>
      <Chrome>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={location.pathname.split("/").slice(0, 2).join("/")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Chrome>
    </ShellCtx.Provider>
  );
};

// Pages still render <StoreLayout> internally. Inside the shell the chrome
// already exists, so pass children straight through; outside it (any standalone
// use) fall back to rendering the full chrome exactly as before.
export const StoreLayout = ({ children }) => {
  const inShell = useContext(ShellCtx);
  if (inShell) return <>{children}</>;
  return <Chrome>{children}</Chrome>;
};
